import { createContext, useContext, useEffect, useState } from 'react';
import { ConfigProvider, theme as antdTheme, Grid, App } from 'antd';
import type { ThemeContext, ThemeProviderProps, ThemeType } from './ThemeProvider.types';
import Utils from 'utils/Utils';

const Context = createContext<ThemeContext | undefined>(undefined);

const isThemeType = (theme: string): theme is ThemeType => {
  return ['light', 'dark'].includes(theme);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContext => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const { useBreakpoint } = Grid;

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const savedTheme = localStorage.getItem('theme');
  const defaultIsCollapsed = localStorage.getItem('navCollapsed') === 'true';
  const preferenceTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
  const userTheme = savedTheme || preferenceTheme;
  const calculatedTheme: ThemeType = isThemeType(userTheme) ? userTheme : 'light';
  const [theme, setTheme] = useState<ThemeType>(calculatedTheme);
  const [isNavCollapsed, setNavCollapsed] = useState<boolean>(defaultIsCollapsed);
  const [openMobileNav, setOpenMobileNav] = useState<boolean>(false);
  const screens = Utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes('lg');

  useEffect(() => {
    document.body.style.backgroundColor = theme === 'dark' ? '#000000' : '#ffffff';
  }, [theme]);

  const toggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };
  return (
    <Context.Provider
      value={{
        theme,
        toggle,
        isNavCollapsed,
        setNavCollapsed,
        openMobileNav,
        setOpenMobileNav,
        isMobile,
      }}
    >
      <ConfigProvider
        theme={{
          algorithm: theme !== 'light' ? antdTheme.darkAlgorithm : undefined,
          components: {
            Upload: {
              pictureCardSize: 140,
            },
          },
        }}
      >
        <App>
          {children}
        </App>
      </ConfigProvider>
    </Context.Provider>
  );
};
